import { CheckCircleIcon } from '@heroicons/react/24/outline'
import CommonCarousel from '@components/common/carousel'
import Button from '@components/atoms/button'

type CriteriaItem = {
  title: string
  description: string
}

type CriteriaCard = {
  heading: string
  bgClass: string
  items: CriteriaItem[]
}

const CRITERIA_CARDS: CriteriaCard[] = [
  {
    heading: 'General requirements',
    bgClass: 'bg-green-50 bg-opacity-50',
    items: [
      {
        title: 'Education requirement',
        description:
          "Bachelor's degree is required. Subject-aligned degrees are preferred for some tracks.",
      },
      {
        title: 'Subject confidence',
        description:
          'Strong fundamentals and the ability to explain clearly matter most.',
      },
      {
        title: 'Communication readiness',
        description:
          'Clear spoken English and structured explanation style are important for K–12 learners.',
      },
      {
        title: 'System readiness',
        description:
          'Laptop or desktop, camera on, stable internet, and a quiet teaching setup are required.',
      },
      {
        title: 'Teaching mindset',
        description:
          'Patience, clarity, and the ability to guide students step by step are important across all subjects.',
      },
    ],
  },
  {
    heading: 'AI & Coding',
    bgClass: 'bg-blue-50 bg-opacity-25',
    items: [
      {
        title: 'Programming fundamentals',
        description:
          'Strong understanding of coding concepts such as logic, sequencing, variables, loops, conditions, and functions.',
      },
      {
        title: 'Tool familiarity',
        description:
          'Comfort with beginner-friendly platforms and coding tools such as Scratch, Python, web development tools, or other age-appropriate platforms.',
      },
      {
        title: 'Project guidance',
        description:
          'Confidence in supporting students while they build games, apps, websites, or basic AI-powered projects.',
      },
      {
        title: 'AI awareness',
        description:
          'Basic understanding of age-appropriate AI concepts and the ability to explain them in a simple, practical way.',
      },
    ],
  },
  {
    heading: 'Math',
    bgClass: 'bg-yellow-100 bg-opacity-50',
    items: [
      {
        title: 'Concept clarity',
        description:
          'Strong understanding of grade-level math concepts with the ability to explain them using simple steps.',
      },
      {
        title: 'Accuracy and reasoning',
        description:
          'Confidence in solving problems correctly and explaining the "why" behind each method.',
      },
      {
        title: 'Multiple explanation styles',
        description:
          "Ability to teach the same concept in different ways based on the student's learning level.",
      },
      {
        title: 'Application mindset',
        description:
          'Comfort in connecting math concepts to real-life examples and problem-solving situations.',
      },
      {
        title: 'Student support',
        description:
          'Ability to identify where a student is getting stuck and guide them with patience and clarity.',
      },
    ],
  },
  {
    heading: 'Public Speaking & Communication',
    bgClass: 'bg-violet-50 bg-opacity-50',
    items: [
      {
        title: 'Language fundamentals',
        description:
          'Strong command of grammar, vocabulary, sentence structure, reading, and writing skills.',
      },
      {
        title: 'Reading and comprehension support',
        description:
          'Ability to help students understand passages, express ideas clearly, and improve interpretation skills.',
      },
      {
        title: 'Speaking and writing guidance',
        description:
          'Confidence in supporting students with spoken English, creative writing, structured writing, and communication skills.',
      },
      {
        title: 'Pronunciation and clarity',
        description:
          'Clear spoken English with good pronunciation, fluency, and student-friendly explanation style.',
      },
      {
        title: 'Feedback ability',
        description:
          'Ability to give simple, constructive feedback that helps students improve confidently.',
      },
    ],
  },
]

const CAROUSEL_RESPONSIVE = {
  desktop: {
    breakpoint: { max: 4000, min: 1024 },
    items: 3,
    partialVisibilityGutter: 40,
  },
  tablet: {
    breakpoint: { max: 1023, min: 640 },
    items: 2,
    partialVisibilityGutter: 30,
  },
  mobile: {
    breakpoint: { max: 639, min: 0 },
    items: 1,
    partialVisibilityGutter: 30,
  },
}

type Props = {
  handleBecomeTeacher: () => void
  ctaText?: string
}

const EligibilityCriteria = ({
  handleBecomeTeacher,
  ctaText = 'Become a teacher',
}: Props) => (
  <div className="px-5 py-12 lg:py-16 bg-orange-100">
    <div className="container mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
          Eligibility criteria
        </h2>
        <p className="text-lg text-grey mx-auto leading-tight">
          Before you start, check the basics.
        </p>
      </div>

      {/* Carousel */}
      <CommonCarousel
        responsive={CAROUSEL_RESPONSIVE}
        autoPlay={false}
        infinite={false}
        partialVisible={true}
      >
        {CRITERIA_CARDS.map((card) => (
          <div
            key={card.heading}
            className={`${card.bgClass} rounded-xl p-6 mx-2 h-full flex flex-col gap-4 border border-grey-500`}
          >
            <h3 className="text-xl font-600 text-grey-900 text-center mb-2">
              {card.heading}
            </h3>
            <ul className="flex flex-col gap-4 list-none p-0">
              {card.items.map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-100 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-600 text-grey-900 text-sm">
                      {item.title}
                    </p>
                    <p className="text-sm text-grey-800 leading-snug">
                      {item.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CommonCarousel>

      {/* Bottom CTA */}
      <div className="mt-10 shadow-md bg-white bg-opacity-50 border border-orange-200 rounded-xl p-4 flex flex-col items-center gap-2 text-center">
        <h2 className="text-2xl text-center text-grey lg:text-3xl font-600">
          Ready to <span className="text-orange">join our</span> teaching
          community?
        </h2>
        <p className="text-lg text-grey-800">
          If you meet the eligibility criteria for your chosen subject track,
          take the next step and apply today.
        </p>
        <Button
          variant="solid"
          intent="primary"
          size="lg"
          onClick={handleBecomeTeacher}
          className="mt-2 w-full sm:w-auto"
        >
          {ctaText}
        </Button>
      </div>
    </div>
  </div>
)

export default EligibilityCriteria
